import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LogIn, AlertCircle, Image, Zap, Lock, BarChart3, ArrowLeft, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { DuplicateLoginModal } from './DuplicateLoginModal';
import { buildKakaoAuthorizeUrl } from '@/lib/socialAuth';
import { SocialConsentModal, SocialConsentAgreements } from './SocialConsentModal';
import { Spinner } from '@/components/ui/spinner';

interface SessionInfo {
  sessionId: string;
  deviceInfo: string;
  ipAddress: string;
  loginTime: string;
  lastActivityTime: string;
  isCurrent: boolean;
}

const isGenericStatusErrorMessage = (message: string): boolean => {
  return /^Request failed with status code \d{3}$/i.test(message.trim());
};

function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  const { login, forceLogin } = useAuth();
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [existingSessions, setExistingSessions] = useState<SessionInfo[]>([]);
  const [showGoogleConsent, setShowGoogleConsent] = useState(false);
  const [pendingGoogleCredential, setPendingGoogleCredential] = useState<string | null>(null);
  const queryParams = new URLSearchParams(location.search);
  const isWithdrawCompleted = queryParams.get('withdrawn') === '1';
  const reJoinableAt = queryParams.get('reJoinableAt');
  const withdrawCompleteMessage = isWithdrawCompleted
    ? `회원 탈퇴가 완료되었습니다.${reJoinableAt ? ` 재가입은 ${reJoinableAt} 이후 가능합니다.` : ' 재가입은 탈퇴일로부터 30일 이후 가능합니다.'}`
    : null;

  // SNS 로그인 실패 에러를 URL에서 읽어서 표시
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlError = params.get('error');
    const storedError = sessionStorage.getItem('socialLoginError');
    const decodedUrlError = urlError ? decodeURIComponent(urlError) : null;

    let nextError = decodedUrlError;
    if ((!nextError || isGenericStatusErrorMessage(nextError)) && storedError) {
      nextError = storedError;
    }

    if (nextError) {
      // SOCIAL_SIGNUP_PENDING| 접두사 제거 (고객에게 노출되지 않도록)
      if (nextError.startsWith('SOCIAL_SIGNUP_PENDING|')) {
        nextError = nextError.substring('SOCIAL_SIGNUP_PENDING|'.length);
      }
      setError(nextError);
    }

    if (storedError) {
      sessionStorage.removeItem('socialLoginError');
    }

    // URL 쿼리 파라미터의 redirect 값을 sessionStorage에 저장
    const redirectParam = params.get('redirect');
    if (redirectParam && !sessionStorage.getItem('redirectAfterLogin')) {
      sessionStorage.setItem('redirectAfterLogin', redirectParam);
    }
  }, [location.search]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result = await login(userId, password, rememberMe);

      // 중복 로그인 감지
      if (result.duplicateLogin && result.existingSessions) {
        setExistingSessions(result.existingSessions);
        setShowDuplicateModal(true);
        setLoading(false);
        return;
      }

      // 로그인 성공 - sessionStorage에서 redirect 경로 확인 후 리다이렉트
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      navigate(redirectPath || '/dashboard');
      // navigate 후 sessionStorage 제거
      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 강제 로그인 확인
  const handleForceLogin = async () => {
    setShowDuplicateModal(false);
    setLoading(true);
    setError(null);

    try {
      await forceLogin(userId, password, rememberMe);
      // 강제 로그인 성공 - sessionStorage에서 redirect 경로 확인 후 리다이렉트
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      navigate(redirectPath || '/dashboard');
      // navigate 후 sessionStorage 제거
      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 강제 로그인 취소
  const handleCancelForceLogin = () => {
    setShowDuplicateModal(false);
    setExistingSessions([]);
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      setError('Google 로그인에 실패했습니다.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await login(credentialResponse.credential, '', rememberMe, 'google');
      // 구글 로그인 성공 - sessionStorage에서 redirect 경로 확인 후 리다이렉트
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      navigate(redirectPath || '/dashboard');
      // navigate 후 sessionStorage 제거
      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin');
      }
    } catch (err: any) {
      // 소셜 신규 가입 → 동의 화면 필요 (SOCIAL_NEW_USER: BE가 신규 유저로 감지)
      if (err.code === 'SOCIAL_SIGNUP_PENDING' || err.code === 'SOCIAL_NEW_USER' || err.message?.startsWith('SOCIAL_SIGNUP_PENDING|')) {
        setPendingGoogleCredential(credentialResponse.credential);
        setShowGoogleConsent(true);
        setLoading(false);
        return;
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleConsentConfirm = async (agreements: SocialConsentAgreements) => {
    if (!pendingGoogleCredential) return;
    setShowGoogleConsent(false);
    setLoading(true);
    setError(null);

    try {
      // BE가 업데이트되면 여기서 consentData를 포함하여 신규 가입 완료 품리트 호출
      await login(pendingGoogleCredential, '', rememberMe, 'google', agreements);
    } catch (err: any) {
      if (err.code === 'SOCIAL_SIGNUP_PENDING' || err.code === 'SOCIAL_NEW_USER' || err.message?.startsWith('SOCIAL_SIGNUP_PENDING|')) {
        const rawMsg = err.message || '';
        const userMsg = rawMsg.startsWith('SOCIAL_SIGNUP_PENDING|')
          ? rawMsg.substring('SOCIAL_SIGNUP_PENDING|'.length)
          : '회원가입 신청이 완료되었습니다. 관리자 승인 후 이용 가능합니다.';
        const pendingEmail = err.data?.email || err.data?.data?.email;
        navigate('/pending-approval', { state: { message: userMsg, email: pendingEmail }, replace: true });
        return;
      }
      setError(err.message || 'Google 소셜 가입 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
      setPendingGoogleCredential(null);
    }
  };

  const handleGoogleError = () => {
    setError('Google 로그인에 실패했습니다.');
  };

  const handleNaverLogin = () => {
    setLoading(true);
    setError(null);
    sessionStorage.removeItem('socialLoginError');
    // 네이버 OAuth 로그인 (백엔드 리다이렉트 URL)
    const clientId = process.env.REACT_APP_NAVER_CLIENT_ID || '';
    const redirectUri = `${window.location.origin}/auth/naver/callback`;
    const state = Math.random().toString(36).substring(7);
    
    sessionStorage.setItem('naverLoginState', state);
    // sessionStorage에서 redirect 경로 확인
    const redirectPath = sessionStorage.getItem('redirectAfterLogin');
    if (redirectPath) {
      sessionStorage.setItem('naverLoginRedirect', redirectPath);
    }
    
    window.location.href = `https://nid.naver.com/oauth2.0/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&state=${state}`;
  };

  const handleKakaoLogin = () => {
    setLoading(true);
    setError(null);
    sessionStorage.removeItem('socialLoginError');
    
    // sessionStorage에서 redirect 경로 확인
    const redirectPath = sessionStorage.getItem('redirectAfterLogin');
    if (redirectPath) {
      sessionStorage.setItem('kakaoLoginRedirect', redirectPath);
    }
    
    window.location.href = buildKakaoAuthorizeUrl();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {/* 좌측: 서비스 소개 */}
        <div className="hidden lg:flex flex-col justify-center items-center p-12 bg-gradient-to-br from-primary to-blue-700 text-white relative overflow-hidden">
          {/* 배경 장식 */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-10 rounded-full -mr-48 -mt-48"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white opacity-10 rounded-full -ml-48 -mb-48"></div>

          <div className="relative z-10 max-w-md">
            <div className="mb-8 flex items-center justify-center">
              <div className="bg-white bg-opacity-40 p-4 rounded-full backdrop-blur-sm shadow-lg">
                <img src="/favicon-32x32.png" alt="ALT Generation Logo" className="w-16 h-16 rounded-full" />
              </div>
            </div>

            <Link to="/" className="block">
              <h1 className="text-5xl font-bold text-center mb-4 hover:text-blue-200 transition-colors cursor-pointer">
                ALT Generation
              </h1>
            </Link>
            <p className="text-xl text-blue-100 text-center mb-12">
              AI 기반 이미지 ALT 텍스트 자동 생성 시스템
            </p>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-white bg-opacity-20">
                    <Zap className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">빠른 생성</h3>
                  <p className="text-blue-100">
                    AI 기술을 이용한 초고속 ALT 텍스트 생성
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-white bg-opacity-20">
                    <Lock className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">안전한 관리</h3>
                  <p className="text-blue-100">
                    엔터프라이즈급 보안으로 데이터 보호
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-white bg-opacity-20">
                    <BarChart3 className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">상세 분석</h3>
                  <p className="text-blue-100">
                    생성된 ALT 텍스트의 품질 및 사용 현황 분석
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-white border-opacity-20">
              <p className="text-blue-100 text-center text-sm">
                웹사이트 크롤링, 이미지 URL, 이미지 업로드 등 <br /> 다양한 방식으로 ALT 텍스트를 생성할 수 있습니다
              </p>
            </div>
          </div>
        </div>

        {/* 우측: 로그인 폼 */}
        <div className="flex flex-col justify-center items-center p-6 md:p-12">
          <div className="w-full max-w-md mb-3">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4" />
              홈으로
            </Link>
          </div>
          <Card className="w-full max-w-md shadow-lg">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4 lg:hidden">
                <div className="bg-white p-3 rounded-lg">
                  <img src="/favicon-32x32.png" alt="ALT Generation Logo" className="w-12 h-12 rounded-full" />
                </div>
              </div>
              <CardTitle className="text-3xl font-bold">로그인</CardTitle>
              <CardDescription className="text-base mt-2">
                ALT Generation에 오신 것을 환영합니다
              </CardDescription>
            </CardHeader>
            <CardContent>
              {withdrawCompleteMessage && (
                <Alert className="mb-4 bg-green-50 border-green-200">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    {withdrawCompleteMessage}
                  </AlertDescription>
                </Alert>
              )}

              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="userId" className="block text-sm font-medium mb-2">
                    아이디
                  </label>
                  <Input
                    id="userId"
                    type="text"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    placeholder="아이디를 입력하세요"
                    disabled={loading}
                    required
                    className="h-10"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium mb-2">
                    비밀번호
                  </label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="비밀번호를 입력하세요"
                      disabled={loading}
                      required
                      className="h-10 pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowPassword((prev) => !prev)}
                      disabled={loading}
                      className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      disabled={loading}
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="text-sm text-gray-700">로그인 상태 유지</span>
                  </label>
                </div>

                <Button 
                  type="submit" 
                  disabled={loading} 
                  className="w-full h-10 text-base font-medium mt-6"
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      로그인 중...
                    </>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4 mr-2" />
                      로그인
                    </>
                  )}
                </Button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">또는</span>
                </div>
              </div>

              <div className="w-full space-y-3">
                {/* Google 로그인 */}
                <div className="flex justify-center">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    text="signin_with"
                    size="large"
                  />
                </div>

                {/* 네이버 & 카카오 로그인 */}
                <div className="flex gap-3 justify-center">
                  <Button
                    type="button"
                    onClick={handleNaverLogin}
                    disabled={loading}
                    className="h-12 px-6 bg-[#2DB400] hover:bg-[#25a000] text-white font-medium flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16.273 12.845L7.727 0h-7.727v24h6.545v-11.155l8.546 11.155h7.727v-24h-6.545v12.845z"/>
                    </svg>
                    네이버
                  </Button>

                  {/* 카카오 로그인 */}
                  <Button
                    type="button"
                    onClick={handleKakaoLogin}
                    disabled={loading}
                    className="h-12 px-6 bg-[#FEE500] hover:bg-[#F0D800] text-[#3A1D1D] font-medium flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#3A1D1D">
                      <path d="M12 3C6.477 3 2 6.477 2 10.737c0 2.738 1.748 5.139 4.38 6.555-.193.718-1.261 4.694-1.298 4.951 0 0-.018.157.082.218.099.061.214.013.214.013.28-.039 3.233-2.121 3.786-2.514A11.82 11.82 0 0012 20.474c5.523 0 10-3.477 10-7.737S17.523 3 12 3z"/>
                    </svg>
                    카카오
                  </Button>
                </div>
              </div>

              <div className="mt-6 text-center space-y-3 text-sm">
                <div className="flex gap-2 justify-center">
                  <Link to="/find-account" className="text-primary hover:underline font-medium">
                    아이디 찾기
                  </Link>
                  <span className="text-gray-300">|</span>
                  <Link to="/find-account" className="text-primary hover:underline font-medium">
                    비밀번호 찾기
                  </Link>
                </div>
                <div>
                  <span className="text-gray-600">계정이 없으신가요? </span>
                  <Link to="/signup" className="text-primary hover:underline font-semibold">
                    회원가입
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 모바일용 서비스 소개 */}
          <div className="mt-8 lg:hidden w-full max-w-md pb-8">
            <div className="bg-gradient-to-br from-primary to-blue-700 rounded-2xl p-6 text-white">
              <div className="text-center mb-5">
                <h2 className="text-lg font-bold">ALT Generation</h2>
                <p className="text-blue-100 text-sm mt-1">AI 기반 이미지 ALT 텍스트 자동 생성 시스템</p>
              </div>
              <div className="space-y-4">
                <div className="flex gap-3 items-start">
                  <div className="flex-shrink-0 flex items-center justify-center h-9 w-9 rounded-lg bg-white/20">
                    <Zap className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">빠른 생성</p>
                    <p className="text-blue-100 text-xs mt-0.5">AI 기술을 이용한 초고속 ALT 텍스트 생성</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <div className="flex-shrink-0 flex items-center justify-center h-9 w-9 rounded-lg bg-white/20">
                    <Lock className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">안전한 관리</p>
                    <p className="text-blue-100 text-xs mt-0.5">엔터프라이즈급 보안으로 데이터 보호</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <div className="flex-shrink-0 flex items-center justify-center h-9 w-9 rounded-lg bg-white/20">
                    <BarChart3 className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">상세 분석</p>
                    <p className="text-blue-100 text-xs mt-0.5">생성된 ALT 텍스트의 품질 및 사용 현황 분석</p>
                  </div>
                </div>
              </div>
              <div className="mt-5 pt-4 border-t border-white/20">
                <p className="text-blue-100 text-center text-xs">
                  웹사이트 크롤링, 이미지 URL, 이미지 업로드 등<br />다양한 방식으로 ALT 텍스트를 생성할 수 있습니다
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 중복 로그인 팝업 */}
      {showDuplicateModal && (
        <DuplicateLoginModal
          existingSessions={existingSessions}
          onConfirm={handleForceLogin}
          onCancel={handleCancelForceLogin}
        />
      )}

      {/* Google 신규 가입 동의 모달 */}
      <SocialConsentModal
        open={showGoogleConsent}
        onOpenChange={(open) => {
          setShowGoogleConsent(open);
          if (!open) setPendingGoogleCredential(null);
        }}
        provider="google"
        onConfirm={handleGoogleConsentConfirm}
      />
    </div>
  );
}

export default Login;
// Fixed: 2026-02-04
